import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { TodayDashboard } from '@rahul365/shared';
import { Button, Card, Metric, ProgressBar, Skeleton } from '@rahul365/ui';
import { Activity, BookOpen, ChevronRight, Droplets, Dumbbell, Footprints, Plus, Scale, Sparkles, Utensils } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../components/Modal.js';
import { Notice } from '../components/Notice.js';
import { api } from '../lib/api.js';
import { localDate } from '../lib/date.js';
import { sendOrQueue } from '../lib/outbox.js';

function display(value: number | null, fallback = '—', digits = 0): string {
  return value === null ? fallback : value.toFixed(digits);
}

export function TodayPage() {
  const date = localDate();
  const navigate = useNavigate();
  const [quickLog, setQuickLog] = useState<'weight' | 'water' | 'walk' | null>(null);
  const dashboard = useQuery({ queryKey: ['today', date], queryFn: () => api<TodayDashboard>(`/api/v1/dashboard/today?date=${date}`) });

  if (dashboard.isLoading) return <TodaySkeleton />;
  if (!dashboard.data) return <Notice tone="warning">Today could not be loaded. Your offline workout remains available from Train.</Notice>;
  const data = dashboard.data;
  const calorieTarget = data.nutrition.calorieTarget ?? 2300;

  return (
    <>
      <header className="today-title">
        <div><span className="eyebrow">RAHUL 365</span><h1>Day {data.dayNumber} <small>/ 365</small></h1><p>{data.displayDate}</p></div>
        <div className="day-ring" style={{ '--day-progress': `${Math.min(100, data.dayNumber / 3.65)}%` } as React.CSSProperties}><span>{Math.round((data.dayNumber / 365) * 100)}%</span></div>
      </header>

      <section className="dashboard-section">
        <div className="section-heading"><div><span className="eyebrow">MORNING SNAPSHOT</span><h2>Recovery context</h2></div><button className="text-button" onClick={() => { void navigate('/journal'); }}>Check in <ChevronRight /></button></div>
        <Card className="snapshot-grid">
          <Metric label="Weight" value={display(data.snapshot.weightKg, 'Log now', 2)} unit={data.snapshot.weightKg === null ? undefined : 'kg'} footnote={data.snapshot.sevenDayAverageWeightKg ? `7-day avg ${data.snapshot.sevenDayAverageWeightKg.toFixed(2)} kg` : 'Trend appears after regular logs'} />
          <Metric label="Sleep" value={display(data.snapshot.sleepHours, '—', 1)} unit={data.snapshot.sleepHours === null ? undefined : 'h'} footnote="Wearable or journal" />
          <Metric label="Resting HR" value={display(data.snapshot.restingHeartRate)} unit={data.snapshot.restingHeartRate === null ? undefined : 'bpm'} footnote="A trend, not a diagnosis" />
          <Metric label="Energy" value={data.snapshot.energy ? `${data.snapshot.energy}/5` : 'Check in'} footnote={data.snapshot.asthmaSymptoms === true ? 'Symptoms logged—train with care' : 'How you feel matters'} />
        </Card>
      </section>

      <div className="dashboard-columns">
        <section className="dashboard-section">
          <span className="eyebrow">TODAY'S PLAN</span>
          <Card className="plan-card">
            <div className="plan-card__art"><Dumbbell /></div>
            <div><span className="eyebrow">FOUNDATION PPL</span><h2>{data.plan.label}</h2><p>{data.plan.detail}</p></div>
            <Button onClick={() => { void navigate(data.plan.routineVersionId ? `/train?start=${data.plan.routineVersionId}` : '/train'); }}><Dumbbell /> {data.plan.routineVersionId ? 'Start workout' : 'Open training'}</Button>
            <div className="plan-card__walk"><Footprints /><span><strong>{data.activity.inclineWalkMinutes} min incline walk</strong><small>Choose easy, moderate or hard</small></span></div>
          </Card>
        </section>

        <section className="dashboard-section">
          <span className="eyebrow">ACTIVITY &amp; NUTRITION</span>
          <Card className="targets-card">
            <div className="target-row"><div><Footprints /><span><strong>{data.activity.steps?.toLocaleString('en-GB') ?? '—'} steps</strong><small>{data.activity.stepsSource ? `Source: ${data.activity.stepsSource}` : 'Connect Health Connect or log activity'}</small></span></div><span>{data.activity.stepTarget.toLocaleString('en-GB')}</span></div>
            <ProgressBar label="Steps" value={data.activity.steps ?? 0} max={data.activity.stepTarget} />
            <div className="target-row"><div><Utensils /><span><strong>{Math.round(data.nutrition.calories)} kcal</strong><small>{data.nutrition.calorieTargetIsTemporary ? 'Temporary starting target' : 'Daily target'}</small></span></div><span>{Math.round(calorieTarget)}</span></div>
            <ProgressBar label="Calories" value={data.nutrition.calories} max={calorieTarget} />
            <div className="target-row"><div><span className="protein-icon">P</span><span><strong>{Math.round(data.nutrition.proteinG)}g protein</strong><small>Consistency across the week</small></span></div><span>{Math.round(data.nutrition.proteinTargetG)}g</span></div>
            <ProgressBar label="Protein" value={data.nutrition.proteinG} max={data.nutrition.proteinTargetG} />
            <div className="target-row"><div><Droplets /><span><strong>{(data.nutrition.waterMl / 1000).toFixed(1)}L water</strong><small>Log what you drink</small></span></div><button className="text-button" onClick={() => setQuickLog('water')}>+ 250 ml</button></div>
          </Card>
        </section>
      </div>

      <section className="dashboard-section">
        <span className="eyebrow">QUICK LOG</span>
        <div className="quick-actions">
          <button onClick={() => setQuickLog('weight')}><Scale /><span>Weight</span><Plus /></button>
          <button onClick={() => { void navigate('/food'); }}><Utensils /><span>Meal</span><Plus /></button>
          <button onClick={() => setQuickLog('water')}><Droplets /><span>Water</span><Plus /></button>
          <button onClick={() => { void navigate('/train'); }}><Dumbbell /><span>Workout</span><Plus /></button>
          <button onClick={() => setQuickLog('walk')}><Footprints /><span>Walk</span><Plus /></button>
          <button onClick={() => { void navigate('/journal'); }}><BookOpen /><span>Journal</span><Plus /></button>
          <button onClick={() => { void navigate('/journal?symptom=1'); }}><Activity /><span>Symptom</span><Plus /></button>
        </div>
      </section>

      <div className="dashboard-columns dashboard-columns--lower">
        <section className="dashboard-section"><span className="eyebrow">COACH</span><Card className="coach-card"><Sparkles /> <div>{data.coachActions.map((action) => <p key={action}>{action}</p>)}</div></Card></section>
        <section className="dashboard-section"><span className="eyebrow">365 PROGRESS</span><Card className="progress-summary"><Metric label="Workouts" value={data.progress.workoutsCompleted} footnote="Completed since Day 1" /><Metric label="Next photos" value={data.progress.nextPhotoDate ?? 'Not set'} footnote="Every four weeks, when useful" /></Card></section>
      </div>

      {quickLog ? <QuickLogModal kind={quickLog} onClose={() => setQuickLog(null)} /> : null}
    </>
  );
}

function QuickLogModal({ kind, onClose }: { kind: 'weight' | 'water' | 'walk'; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(kind === 'water' ? '250' : kind === 'walk' ? '40' : '');
  const [status, setStatus] = useState('');
  const submit = async () => {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return setStatus('Enter a valid amount.');
    const clientId = crypto.randomUUID();
    const result = await sendOrQueue({
      idempotencyKey: `${kind}:${clientId}`,
      path: kind === 'weight' ? '/api/v1/progress/weight' : kind === 'water' ? '/api/v1/water' : '/api/v1/activity',
      method: 'POST',
      body: kind === 'weight'
        ? { clientId, measuredAt: new Date().toISOString(), weightKg: number, source: 'manual', notes: '' }
        : kind === 'water'
          ? { clientId, occurredAt: new Date().toISOString(), amountMl: number }
          : { clientId, startedAt: new Date(Date.now() - number * 60_000).toISOString(), endedAt: new Date().toISOString(), durationMinutes: number, perceivedEffort: 2, notes: '', kind: 'walk', distanceKm: null },
    });
    setStatus(result.queued ? 'Saved offline. It will sync automatically.' : 'Saved.');
    await queryClient.invalidateQueries({ queryKey: ['today'] });
    setTimeout(onClose, 600);
  };
  return <Modal title={kind === 'weight' ? 'Log morning weight' : kind === 'water' ? 'Add water' : 'Log walk'} onClose={onClose}><p>{kind === 'weight' ? 'After toilet, before breakfast—consistency matters more than exact conditions.' : kind === 'walk' ? 'A 35–45 minute walk is the default; any useful duration counts.' : 'A useful estimate is enough.'}</p><div className="input-with-unit"><input className="input numeric-input" autoFocus inputMode="decimal" type="number" step={kind === 'weight' ? '0.05' : kind === 'water' ? '50' : '1'} value={value} onChange={(event) => setValue(event.target.value)} /><span>{kind === 'weight' ? 'kg' : kind === 'water' ? 'ml' : 'min'}</span></div>{status ? <p className="form-status">{status}</p> : null}<Button onClick={() => void submit()}>Save {kind}</Button></Modal>;
}

function TodaySkeleton() {
  return <div className="today-skeleton"><Skeleton className="skeleton--title" /><Card><Skeleton /><Skeleton /><Skeleton /></Card><div className="dashboard-columns"><Card><Skeleton /><Skeleton /></Card><Card><Skeleton /><Skeleton /></Card></div></div>;
}
